import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import SuggestedActionsTable from './components/SuggestedActionsTable';
import {
  COLUMNS,
  DEFAULT_SORT,
  HEADING_TRANSLATIONS,
  MULTI_SORT_VALUES,
  SuggestedActionsTableData,
  LOCKED_COLUMN,
} from './components/SuggestedActionsTable/data';
import { TABLES_WITH_COLUMN_SETTINGS } from 'models/User';
import useTableColumnSettings from 'components/ReorderResizeTable/hooks/useTableColumnSettings';
import useQueryParams from 'hooks/useQueryParams';
import api from 'api';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import MultiFilterPanel from 'components/MultiFilterPanel';
import MultiSortPanel from 'components/MultiSortPanel';
import ManageColumns from 'components/ManageColumns';
import Subnav from 'components/Subnav';
import { useTranslation } from 'react-i18next';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import useGetFilterOptions from 'hooks/useGetFilterOptions';
import useGetSearchableFilterOptions from '../../hooks';
import { useBossModeStatsLinks } from '../../useBossModeStatsLinks';
import {
  DEAL_FILTER_KEY,
  DEAL_TITLE_FILTER_KEY,
  TIMEFRAME_FILTER,
  USER_ID_FILTER_KEY,
  USER_NAME_FILTER_KEY,
  BossModeSuggestedActionsFilters,
} from '../../types';
import { FilterOption } from 'models/Filters';
import { TIMEFRAME_PARAM_KEYWORD } from 'data/global';
import { SORTING_ORDER, SortingDirectionValues } from 'models/Sort';
import { ReadablePendingActionType } from './components/SuggestedActionsDrawer/types';
import useTableFetchState from 'hooks/useTableFetchState';
import TableExportPanel from 'components/TableExportPanel';
import { getTableExportRowsAndColumnsParams } from 'components/TableExportPanel/TableExportPanel.utils';
import { TableExportSelection } from 'models/TableExport';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DownloadStatusContext from 'providers/DownloadStatus/DownloadStatus.context';
import downloadService from 'services/downloadService';
import { getArrayLength, getHasActiveFilters, getIsArrayEmpty } from 'utils';
import useChipLabelClickRefs from 'hooks/useChipLabelClickRefs';

import './SuggestedActions.styles.scss';

type SuggestedActionsProps = {
  className?: string;
};

const SuggestedActions: React.FC<SuggestedActionsProps> = (props) => {
  const { className } = props;

  const {
    isLoading,
    totalPages,
    totalItems,
    data: items,
    setIsLoading,
    setData,
    setPaginatedData,
  } = useTableFetchState<SuggestedActionsTableData>();

  const { popServerError } = useContext(PopNotificationsContext);
  const {
    isBossModeExportInProgress,
    startBossModeExportDownload,
    failBossModeExportDownload,
  } = useContext(DownloadStatusContext);

  const { limit, updateLimit } = useNumberOfResultsPerPage();
  const {
    params,
    params: { page, sort = DEFAULT_SORT, userId, dealRoomIds, timeframe },
  } = useQueryParams();

  const {
    multiFilterRef,
    multiSortRef,
    onChipLabelClick,
    onSortChipLabelClick,
  } = useChipLabelClickRefs();

  const classes = classNames('suggested-actions', className);

  const { t } = useTranslation();

  const createSelectOptions = useCreateSelectOptions();

  const { getMultiSelectSearchableFilter } = useGetFilterOptions();
  const { getDealOptions, getAllUserOptions } = useGetSearchableFilterOptions();

  const links = useBossModeStatsLinks();

  const {
    columns,
    allColumns,
    hiddenColumns,
    columnSizing,
    isDefaultColumns,
    onReorder,
    onColumnSizingChange,
    saveColumnSettings,
    resetToDefault,
  } = useTableColumnSettings(
    TABLES_WITH_COLUMN_SETTINGS.SUGGESTED_ACTIONS,
    COLUMNS,
  );

  const ownerFilterOption = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('owner'),
        USER_ID_FILTER_KEY,
        USER_NAME_FILTER_KEY,
        getAllUserOptions,
      ) as FilterOption,
    [getMultiSelectSearchableFilter, getAllUserOptions, t],
  );

  const dealFilterOption = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('deal'),
        DEAL_FILTER_KEY,
        DEAL_TITLE_FILTER_KEY,
        getDealOptions,
      ) as FilterOption,
    [getDealOptions, getMultiSelectSearchableFilter, t],
  );

  const timeFilterOption = useMemo(
    () => ({
      value: TIMEFRAME_PARAM_KEYWORD,
      label: t('lastActivity'),
      shouldSortOptions: false,
      options: createSelectOptions(Object.values(TIMEFRAME_FILTER)),
    }),
    [createSelectOptions, t],
  );

  const filterOptions = useMemo(
    () => [ownerFilterOption, dealFilterOption, timeFilterOption],
    [dealFilterOption, ownerFilterOption, timeFilterOption],
  );

  const isEmpty = !isLoading && getIsArrayEmpty(items);

  const hasActiveFilters = useMemo(
    () => getHasActiveFilters(filterOptions, params),
    [filterOptions, params],
  );

  const columnFilterOptions = useMemo(
    () => ({
      dealName: { options: dealFilterOption },
      owner: { options: ownerFilterOption },
      lastUpdated: { options: timeFilterOption },
    }),
    [dealFilterOption, ownerFilterOption, timeFilterOption],
  );

  const translatedSortOptions = useMemo(
    () =>
      MULTI_SORT_VALUES.map(({ value, label }) => ({
        value,
        label: t(label),
      })),
    [t],
  );

  const multiSortDirectionTranslation = useMemo<SortingDirectionValues>(
    () => ({
      lastActivity: {
        [SORTING_ORDER.A_TO_Z]: t('oldestFirst'),
        [SORTING_ORDER.Z_TO_A]: t('recentFirst'),
      },
    }),
    [t],
  );

  // Mirrors the drawer's "Mark as read" (and its undo) on the table row pills.
  const onPendingActionsChange = useCallback(
    (
      suggestedActionId: string,
      type: ReadablePendingActionType,
      count: number,
    ) =>
      setData((old) =>
        old.map((item) =>
          item._id !== suggestedActionId
            ? item
            : {
                ...item,
                pendingActions: { ...item.pendingActions, [type]: count },
              },
        ),
      ),
    [setData],
  );

  const onExport = useCallback(
    async ({ rows, columns: columnsScope, format }: TableExportSelection) => {
      try {
        startBossModeExportDownload();

        await api.portfolio.admin.exportBossModeSuggestedActions({
          userId,
          dealRoomIds,
          timeframe,
          $sort: sort,
          ...getTableExportRowsAndColumnsParams({
            rows,
            columnsScope,
            columns,
            page,
            limit,
          }),
          format,
          uniqueId: downloadService.downloadUniqueId,
        });
      } catch (e) {
        popServerError(e);
        failBossModeExportDownload();
      }
    },
    [
      userId,
      dealRoomIds,
      timeframe,
      sort,
      page,
      limit,
      columns,
      popServerError,
      startBossModeExportDownload,
      failBossModeExportDownload,
    ],
  );

  const getResultsCount = useCallback(
    ({ userId, dealRoomIds, timeframe }: BossModeSuggestedActionsFilters) =>
      api.portfolio.admin
        .getBossModeSuggestedActionsCount({ userId, dealRoomIds, timeframe })
        .then(({ data }) => data.count),
    [],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const { data } = await api.portfolio.admin.getBossModeSuggestedActions({
          $page: page,
          $limit: limit,
          $sort: sort,
          userId,
          dealRoomIds,
          timeframe,
        });
        setPaginatedData(data);
      } catch (e) {
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    page,
    limit,
    sort,
    userId,
    dealRoomIds,
    timeframe,
    setPaginatedData,
    setIsLoading,
  ]);

  return (
    <div className={classes}>
      <div className="operations-row">
        <Subnav links={links} />

        <div className="filter-wrapper">
          {(!isEmpty || hasActiveFilters) && (
            <>
              {!isEmpty && (
                <NumberOfResultsPerPageFilter
                  limit={limit}
                  updateLimit={updateLimit}
                />
              )}
              <MultiFilterPanel
                ref={multiFilterRef}
                filters={filterOptions}
                getResultsCount={getResultsCount}
              />
              <MultiSortPanel
                ref={multiSortRef}
                options={translatedSortOptions}
                sortDirectionTranslations={multiSortDirectionTranslation}
              />
            </>
          )}
          <ManageColumns
            allColumns={allColumns}
            hiddenColumns={hiddenColumns}
            headingTranslations={HEADING_TRANSLATIONS}
            lockedColumn={LOCKED_COLUMN}
            isDefaultColumns={isDefaultColumns}
            onSave={saveColumnSettings}
            onResetToDefault={resetToDefault}
          />
          <TableExportPanel
            totalItems={totalItems}
            pageItemsCount={getArrayLength(items)}
            totalPages={totalPages}
            totalColumnsCount={getArrayLength(COLUMNS)}
            visibleColumnsCount={getArrayLength(columns)}
            isExporting={isBossModeExportInProgress}
            onExport={onExport}
          />
        </div>
      </div>

      <ActiveFilterSortPills
        filterOptions={filterOptions}
        sortOptions={translatedSortOptions}
        onChipLabelClick={onChipLabelClick}
        onSortChipLabelClick={onSortChipLabelClick}
      />

      <SuggestedActionsTable
        isLoading={isLoading}
        data={items}
        sort={sort}
        totalPages={totalPages}
        totalItems={totalItems}
        columns={columns}
        columnFilterOptions={columnFilterOptions}
        columnSizing={columnSizing}
        onColumnSizingChange={onColumnSizingChange}
        onReorder={onReorder}
        onPendingActionsChange={onPendingActionsChange}
      />
    </div>
  );
};

export default SuggestedActions;
